import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { ReactComponent as FireIcon } from '../../../../assets/icons/trainings/fire.svg';
import AddActionButton from '../../../../components/designe/ActionButtons/AddActionButton';
import EditActionButton from '../../../../components/designe/ActionButtons/EditActionButton/EditActionButton';
import SaveActionButton from '../../../../components/designe/ActionButtons/SaveActionButton';
import AppDialog from '../../../../components/reusable/app-dialog/AppDialog';
import ClickableExercise from '../../../../components/reusable/cliackable-exercise/ClickableExercise';
import ConfirmDialog from '../../../../components/reusable/confirm-dialog/ConfirmDialog';
import SaveSet from '../../../../components/reusable/set-group-edit-table/SetEdit/save-set/SaveSet';
import { useUserSetPreferences } from '../../../../hooks/use-user-set-preferences';
import { useElementWidth } from '../../../../hooks/useElementWidth';
import { usePutTrainingSetLoad } from '../../../../redux/api/trainingsAPI';
import { sideColumnWidth, typeColumnWidth } from '../../../../shared/constants';
import { IExercise } from '../../../../types/interfaces/exercise';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../../types/interfaces/training-set-item';
import { orderBy } from '../../../../utils/collections/array-utils';
import {
  getSetItemKey,
  orderOptions,
} from '../../../../utils/training/set-utils';
import {
  getColumnOptions,
  getColumnWidth,
} from '../../../../utils/ui/column-size-utils';
import { NewExerciseModal } from '../../../exercises/new-exercise/NewExerciseModal';
import CurrentTrainingSetItem from '../current-training-set-item/CurrentTrainingSetItem';
import { useStyles } from './CurrentTrainingSet.styles';
import EditManualLoad from './edit-manual-load/EditManualLoad';
import { useCurrentTrainingSet } from './useCurrentTrainingSet';

interface ICurrentTrainingSetGroupProps {
  trainingSet: ITrainingSet;
  trainingId: string;
  activateTimer: (value: number) => void;
  groupActions?: React.ReactNode;
}

function trainingSetJsonEqual(a: ITrainingSet, b: ITrainingSet): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function areCurrentTrainingSetPropsEqual(
  prev: ICurrentTrainingSetGroupProps,
  next: ICurrentTrainingSetGroupProps,
): boolean {
  return (
    prev.trainingId === next.trainingId &&
    trainingSetJsonEqual(prev.trainingSet, next.trainingSet)
  );
}

const actionsWidth = 32;

const roundToTwoDecimals = (value: number) => {
  return Math.round(value * 100) / 100;
};

function CurrentTrainingSetInner(props: ICurrentTrainingSetGroupProps) {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { trainingSet, trainingId, activateTimer, groupActions } = props;
  const { t } = useTranslation();
  const [showEditLoadModal, setShowEditLoadModal] = useState(false);
  const [putTrainingSetLoad] = usePutTrainingSetLoad();
  const { showSaveSetButton } = useUserSetPreferences();
  const {
    exerciseOptions,
    onDeleteTrainingSetItem,
    // exercises,
    trainingSetFormik,
    // mode,
    // setMode,
    onAddTrainingSetItemClick,
    showNewExerciseModal,
    setShowNewExerciseModal,
    setDeleting,
    setSetDeleting,
    onDeleteTrainingSetConfirm,
    showSaveSetModal,
    setShowSaveSetModal,
    onSaveSet,
    finishedItemsCount,
    doLateItemsCount,
    skippedItemsCount,
    notStartedItemsCount,
  } = useCurrentTrainingSet(trainingSet);

  const itemsCount = trainingSet.trainingSetItems.length;
  const handleSaveManualLoad = async (manualLoad: number | null) => {
    await putTrainingSetLoad({
      trainingId,
      setId: trainingSet.id,
      manualLoad,
    }).unwrap();
    setShowEditLoadModal(false);
  };

  const { ref, width } = useElementWidth();
  const columnWidth = getColumnWidth(
    width,
    trainingSet?.exercise?.options?.length,
    typeColumnWidth +
      (!trainingSet?.exercise?.splitBySide ? 0 : sideColumnWidth) +
      actionsWidth,
  );

  function renderExerciseOptionHeaders(trainingSetItem: ITrainingSetItem) {
    const orderedOptions = orderOptions(
      trainingSetItem?.trainingSetItemOptions,
      exerciseOptions,
    );

    return (
      <Box
        display="flex"
        alignItems="center"
        className={classes.trainingSetItemListHeader}>
        <Box
          {...getColumnOptions(typeColumnWidth)}
          className={classes.headerTextWrapper}>
          <Typography variant="label-text-md"> {t('common.type')}</Typography>
        </Box>
        {trainingSet?.exercise?.splitBySide && (
          <Box
            {...getColumnOptions(sideColumnWidth)}
            className={classes.headerTextWrapper}
          />
        )}
        {orderedOptions.map((setOption) => {
          const option = exerciseOptions?.find(
            (e) => e.id === setOption.optionId,
          );
          return (
            <Box
              {...getColumnOptions(columnWidth)}
              key={option?.id}
              className={classes.headerTextWrapper}>
              <Typography variant="label-text-md">{option?.name}</Typography>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box key={trainingSet.id}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        className={classes.trainingSetHeader}
        minWidth={0}>
        <Box flex={1} minWidth={0}>
          <ClickableExercise
            exercise={trainingSet.exercise}
            fromTrainingId={trainingId}
            progressLabel={`${finishedItemsCount}/${itemsCount}`}
          />
        </Box>
        {!!groupActions && (
          <Box position="relative">
            <Box
              position="absolute"
              top={theme.spacing(-1)}
              right={0}
              display="flex"
              gap={1}
              alignItems="center"
              flexShrink={0}>
              {groupActions}
              {showSaveSetButton && trainingSet?.exerciseId && (
                <SaveActionButton onClick={() => setShowSaveSetModal(true)} />
              )}
            </Box>
          </Box>
        )}
      </Box>
      <Box className={classes.trainingSetProgressContainer}>
        <Box
          className={clsx(
            classes.trainingSetProgress,
            classes.trainingSetProgressCompleted,
          )}
          style={{ maxWidth: `${(finishedItemsCount / itemsCount) * 100}%` }}
        />
        <Box
          className={clsx(
            classes.trainingSetProgress,
            classes.trainingSetProgressDoLate,
          )}
          style={{ maxWidth: `${(doLateItemsCount / itemsCount) * 100}%` }}
        />
        <Box
          className={clsx(
            classes.trainingSetProgress,
            classes.trainingSetProgressSkipped,
          )}
          style={{ maxWidth: `${(skippedItemsCount / itemsCount) * 100}%` }}
        />
      </Box>
      <Box className={classes.trainingSetItemList} ref={ref}>
        {renderExerciseOptionHeaders(trainingSet.trainingSetItems[0])}
        {orderBy(trainingSet.trainingSetItems, 'orderNumber').map(
          (trainingSetItem: ITrainingSetItem) => (
            <CurrentTrainingSetItem
              key={getSetItemKey(trainingSetItem)}
              trainingSet={trainingSet}
              trainingSetItem={trainingSetItem}
              onActivateTimer={activateTimer}
              trainingId={trainingId}
              columnWidth={columnWidth}
              onItemDelete={onDeleteTrainingSetItem}
            />
          ),
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          className={classes.trainingSetFooter}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" gap={0.25}>
              <FireIcon className={classes.fireIcon} />
              <Typography variant="label-text-md">
                {t('trainings.totalLoad')}:{' '}
              </Typography>
            </Box>
            <Typography variant="label-text-md">
              {trainingSet.manualLoad !== null &&
              trainingSet.manualLoad !== undefined
                ? roundToTwoDecimals(trainingSet.manualLoad)
                : roundToTwoDecimals(trainingSet.load) || '-'}
            </Typography>
            <EditActionButton onClick={() => setShowEditLoadModal(true)} />
          </Box>
          <AddActionButton
            onClick={() => onAddTrainingSetItemClick()}></AddActionButton>
        </Box>
      </Box>
      <ConfirmDialog
        open={!!setDeleting}
        onClose={() => setSetDeleting(false)}
        title={t('common.delete')}
        onSubmit={() => {
          onDeleteTrainingSetConfirm();
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>

      <NewExerciseModal
        showNewExerciseModal={showNewExerciseModal}
        setShowNewExerciseModal={setShowNewExerciseModal}
        onExerciseCreate={(exercise: IExercise) =>
          trainingSetFormik.setFieldValue('exercise', exercise)
        }
      />
      <AppDialog
        open={!!showSaveSetModal}
        onClose={() => setShowSaveSetModal(false)}
        title={t('setTable.saveSet')}>
        <SaveSet
          onCancel={() => setShowSaveSetModal(false)}
          onSaveSet={(title: string) => onSaveSet(title)}
        />
      </AppDialog>
      <AppDialog
        open={showEditLoadModal}
        onClose={() => setShowEditLoadModal(false)}
        title={t('trainings.manualLoad')}>
        <EditManualLoad
          onCancel={() => setShowEditLoadModal(false)}
          onSave={handleSaveManualLoad}
          currentValue={
            trainingSet.manualLoad !== null &&
            trainingSet.manualLoad !== undefined
              ? trainingSet.manualLoad
              : trainingSet.load
          }
        />
      </AppDialog>
    </Box>
  );
}

export const CurrentTrainingSet = memo(
  CurrentTrainingSetInner,
  areCurrentTrainingSetPropsEqual,
);
